import { App, AppState } from "@capacitor/app";
import { PluginListenerHandle } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import React from "react";
import { connect } from "react-redux";
import { Redirect, RouteComponentProps, withRouter } from "react-router-dom";
import { RootState, actions } from "../../store/index";
import { APIUserInterface } from "../../store/user/actions";

import {
  IonButton,
  IonGrid,
  IonNote,
  IonRow,
  IonSpinner,
  IonText,
  withIonLifeCycle,
} from "@ionic/react";

import { Device } from "@capacitor/device";
import { QRCodeSVG } from "qrcode.react";
import LimeAPI from "../../classes/LimeAPI";
import { logEvent } from "../../helpers/analytics";
import { consumeLoginReturnPath, clearSigningSession } from "../../helpers/signing-session";

interface LoginFormInterface {
  user: object;
  pin: string;
  loginActive: boolean;
  inputValid: boolean;
  ssn: string;
  rememberMe: boolean;
  sameDevice: boolean;
  errorMsg: string;
  isLoading: boolean;
  loginProcessActive: boolean;
  loginProcessFailed: boolean;
  loginSuccess: boolean;
  bankIdMessage: string;
  orderRef: string;
  debugText: string;
  qrData?: string;
  loginReturnPath: string;
}

interface OwnProps {
  history: any;
}

interface StateProps {
  user: object;
}

interface DispatchProps {
  setUser: (object: any) => void;
}

type LoginFormProps = StateProps &
  DispatchProps &
  OwnProps &
  RouteComponentProps;

class LoginForm extends React.Component<LoginFormProps, LoginFormInterface> {
  appStateListener: PluginListenerHandle | null = null;
  intervalId: any;
  timeoutId: any;
  checkOrderRefStatus: any;
  redirectTimeoutId: any;
  authTimeoutId: any;
  lastPlatform: string = "";
  abortController = new AbortController();
  startAbortController: AbortController | null = null;
  _isMounted = false;
  ORDER_REF_MAX_AGE_MS = 3 * 60 * 1000;
  AUTH_START_TIMEOUT_MS = 45 * 1000;
  constructor(props: LoginFormProps) {
    super(props);

    this.state = {
      loginActive: false,
      sameDevice: false,
      user: {},
      pin: "",
      inputValid: false,
      ssn: "",
      rememberMe: false,
      errorMsg: "",
      isLoading: false,
      loginProcessActive: false,
      loginProcessFailed: false,
      loginSuccess: false,
      bankIdMessage: "",
      orderRef: "",
      debugText: "",
      qrData: undefined,
      loginReturnPath: "/projects",
    };

    this.handleInputCallback = this.handleInputCallback.bind(this);
    this.handleToggleCallback = this.handleToggleCallback.bind(this);
    this.login = this.login.bind(this);
    this.cancelLoginProcess = this.cancelLoginProcess.bind(this);
  }

  getQueryParams(param: string): string | null {
    const params = new URLSearchParams(this.props.location.search);
    return params.get(param);
  }

  clearStoredOrderRef() {
    localStorage.removeItem("orderRef");
    localStorage.removeItem("orderRefCreatedAt");
  }

  getFreshStoredOrderRef(): string | null {
    const orderRef = localStorage.getItem("orderRef");
    const orderRefCreatedAt = Number(
      localStorage.getItem("orderRefCreatedAt") || 0,
    );
    const orderRefIsFresh =
      orderRefCreatedAt > 0 &&
      Date.now() - orderRefCreatedAt < this.ORDER_REF_MAX_AGE_MS;

    if (orderRef && orderRefIsFresh) {
      return orderRef;
    }

    if (orderRef) {
      this.clearStoredOrderRef();
    }

    return null;
  }

  async componentDidMount() {
    this._isMounted = true;
    this.getPinFromStorage().catch(() => false);

    App.addListener("appStateChange", this.handleAppStateChange)
      .then((listener) => {
        this.appStateListener = listener;
      })
      .catch(() => {
        this.appStateListener = null;
      });

    const freshOrderRef = this.getFreshStoredOrderRef();
    if (freshOrderRef) {
      this.resumeStoredAuth(freshOrderRef);
    }

    const sessionId = this.getQueryParams("sessionId");
    if (sessionId) {
      this.checkLoginStatus(sessionId);
    }

    const info = await Device.getInfo();
    actions.deviceInfo.setPlatform(info.platform);

    if (
      this.props.location.pathname === "/login-review-test-for-ios-and-android"
    ) {
      this.safeSetState({
        pin: "199102224939",
        inputValid: true,
        loginActive: true,
      });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.startAbortController?.abort();
    this.abortController.abort();
    this.appStateListener?.remove();
    this.clearIntervals();
  }

  componentWillLeave() {
    this.safeSetState({ errorMsg: "" });
  }

  safeSetState(state: any) {
    if (this._isMounted) {
      this.setState(state);
    }
  }

  getStoredSameDevice(): boolean {
    try {
      const storedSameDevice = localStorage.getItem("sameDevice");

      if (storedSameDevice != null) {
        return JSON.parse(storedSameDevice) === true;
      }
    } catch {
      return this.state.sameDevice;
    }

    return this.state.sameDevice;
  }

  resumeStoredAuth(orderRef: string) {
    const sameDevice = this.getStoredSameDevice();

    this.safeSetState({
      orderRef,
      loginProcessActive: true,
      isLoading: true,
      sameDevice,
      bankIdMessage:
        this.state.bankIdMessage || "Legitimera dig i din BankID-app.",
    });

    if (this.intervalId || this.checkOrderRefStatus) {
      this.checkLoginStatus(orderRef, !sameDevice);
      return;
    }

    if (sameDevice) {
      this.startSameDevicePolling(orderRef);
      return;
    }

    this.startIntervals(orderRef);
  }

  handleAppStateChange = (state: AppState) => {
    if (!state.isActive) {
      return;
    }

    const freshOrderRef = this.getFreshStoredOrderRef();
    if (!freshOrderRef) {
      if (this.state.loginProcessActive || this.state.orderRef) {
        this.clearIntervals();
        this.safeSetState({
          qrData: undefined,
          loginProcessActive: false,
          isLoading: false,
          orderRef: "",
        });
      }
      return;
    }

    this.resumeStoredAuth(freshOrderRef);
  };

  async getPinFromStorage() {
    const userPin: any = await Preferences.get({ key: "userPin" });

    if (userPin.hasOwnProperty("value") && this._isMounted) {
      const hasValue = userPin.value !== null;
      this.safeSetState({
        pin: hasValue ? userPin.value : "",
        inputValid: hasValue && userPin.value.length === 12,
        rememberMe: hasValue,
        loginActive: hasValue,
      });
    }
  }

  async checkLoginStatus(orderRef: any, qrCode = false) {
    const userStatus = await LimeAPI.post(
      "scrive/authenticateUser/checkUserStatus",
      { orderRef, sameDevice: this.state.sameDevice },
      this.abortController.signal,
    );

    if (userStatus && (userStatus.aborted || userStatus.error)) {
      return;
    }

    try {
      const { data } = userStatus;

      if (data.message) {
        this.safeSetState({ bankIdMessage: data.message });
      }

      if (data.status === "Finished") {
        this.clearIntervals();
        this.clearStoredOrderRef();

        if (!data.signature || !data.pin) {
          this.failAuth(
            "Inloggningen kunde inte slutföras. Försök igen.",
            "missing_credentials",
            {
              bodySnippet: JSON.stringify({
                hasSignature: Boolean(data.signature),
                hasPin: Boolean(data.pin),
              }),
            },
          );
          return;
        }

        await this.loginUser(data.signature, data.pin);
        this.safeSetState({ loginProcessFailed: false, isLoading: false });

        return;
      }

      if (data.retry === false) {
        this.clearIntervals();
        this.clearStoredOrderRef();
        this.safeSetState({
          qrData: undefined,
          loginProcessActive: false,
          loginProcessFailed: true,
          isLoading: false,
          orderRef: "",
          errorMsg: data.message || "Inloggningen misslyckades. Försök igen.",
        });
        return;
      }

      if (qrCode && data.qrData) {
        this.safeSetState({
          qrData: data.qrData,
          isLoading: false,
          loginProcessActive: true,
        });
      }
    } catch {
      this.safeSetState({
        errorMsg: "Något gick fel, försök igen.",
        isLoading: false,
        loginProcessFailed: true,
      });
    }
  }

  async authenticate(sameDevice: boolean, test = false) {
    this.clearIntervals();
    this.startAbortController?.abort();
    this.startAbortController = new AbortController();
    const startController = this.startAbortController;

    this.abortController.abort();
    this.abortController = new AbortController();

    this.safeSetState({ bankIdMessage: "Legitimera dig i din BankID-app." });
    const info = await Device.getInfo();
    this.lastPlatform = info.platform;
    actions.deviceInfo.setPlatform(info.platform);

    this.authTimeoutId = setTimeout(() => {
      startController.abort();
    }, this.AUTH_START_TIMEOUT_MS);

    LimeAPI.post(
      `scrive/authenticateUser`,
      { sameDevice, platform: info.platform },
      startController.signal,
    )
      .then((data) => {
        clearTimeout(this.authTimeoutId);

        if (data && data.aborted) {
          this.failAuth(
            "BankID-tjänsten svarar inte just nu. Försök igen.",
            "timeout",
            data,
          );
          return;
        }

        if (!data || !data.success) {
          const message =
            (data && data.data && data.data.message) ||
            "BankID-tjänsten svarar inte just nu. Försök igen.";
          this.failAuth(
            message,
            data && data.error ? "network_error" : "api_error",
            data || {},
          );
          return;
        }

        this.safeSetState({
          loginProcessActive: true,
          isLoading: true,
          orderRef: data.data.orderRef,
          sameDevice: data.data.same_device,
        });
        localStorage.setItem("orderRef", data.data.orderRef);
        localStorage.setItem("orderRefCreatedAt", Date.now().toString());
        localStorage.setItem(
          "sameDevice",
          JSON.stringify(data.data.same_device),
        );

        if (data.data.redirect_url) {
          this.startSameDevicePolling(data.data.orderRef);
          this.openBankId(data.data.redirect_url);
        } else if (data.data.qrData) {
          this.safeSetState({
            qrData: data.data.qrData,
            isLoading: false,
            loginProcessActive: true,
          });
          this.startIntervals(data.data.orderRef);
        } else {
          this.failAuth(
            "Kunde inte hämta QR-koden. Försök igen.",
            "missing_qr",
          );
        }
      })
      .catch(() => {
        clearTimeout(this.authTimeoutId);
        this.failAuth("Något gick fel, försök igen.", "exception");
      });
  }

  startSameDevicePolling(orderRef: string) {
    this.checkLoginStatus(orderRef);
    this.checkOrderRefStatus = setInterval(() => {
      this.checkLoginStatus(orderRef);
    }, 1000);
    this.redirectTimeoutId = setTimeout(() => {
      this.clearIntervals();
      this.clearStoredOrderRef();
      this.safeSetState({
        qrData: undefined,
        loginProcessActive: false,
        isLoading: false,
        orderRef: "",
      });
    }, this.ORDER_REF_MAX_AGE_MS);
  }

  openBankId(url: string) {
    window.location.href = url;
  }

  async login(event: any) {
    event.preventDefault();

    this.safeSetState({ isLoading: true, errorMsg: "", loginProcessFailed: false });

    const pin =
      this.state.pin ||
      (process.env.REACT_APP_BANKID !== "true"
        ? process.env.REACT_APP_TEST_ACCOUNT
        : null) ||
      "";
    const formattedPin = `${pin.slice(0, 8)}-${pin.slice(8)}`;

    const isTestPage =
      this.props.location.pathname === "/login-review-test-for-ios-and-android";

    if (
      !isTestPage &&
      process.env.REACT_APP_BANKID === "true" &&
      pin !== process.env.REACT_APP_TEST_ACCOUNT
    ) {
      this.authenticate(true);
    } else {
      const seconds = new Date().getTime() / 1000;
      const token = `some-random-string-021201lf-test${seconds}`;
      await this.loginUser(token, formattedPin); // only for test
    }
  }

  async bankID(sameDevice: boolean, test = false) {
    this.safeSetState({
      isLoading: true,
      errorMsg: "",
      loginProcessFailed: false,
      loginProcessActive: true,
    });
    if (
      process.env.REACT_APP_BANKID === "true" &&
      this.props.location.pathname !== "/login-review-test-for-ios-and-android"
    ) {
      this.authenticate(sameDevice, test);
    } else {
      this.login({ preventDefault: () => {} });
    }
  }

  async loginUser(authId: string, formattedPin: string) {
    logEvent("logged_in");
    try {
      const response = await LimeAPI.post(
        `personByPin`,
        { pin: formattedPin },
        this.abortController.signal,
      );

      if (!response.success) {
        this.safeSetState({ errorMsg: response.data.message, isLoading: false });
        return;
      }

      await this.setServerSession(authId, response, formattedPin);
    } catch {
      this.safeSetState({
        errorMsg: "Något gick fel, försök igen.",
        isLoading: false,
      });
    }
  }

  async setServerSession(
    authId: string,
    personData: any,
    formattedPin: string,
  ) {
    try {
      const yearDigits = `${formattedPin.slice(0, 2)}`;

      const response = await LimeAPI.post(
        `authenticateUser/storeUserSession`,
        {
          authId,
          userId: personData.person._id,
          companyId: personData.person.company,
          position: personData.person.position.key,
          year: yearDigits,
        },
        this.abortController.signal,
      );
      if (!response.success) {
        this.safeSetState({ errorMsg: response.data.message, isLoading: false });
        return;
      }

      window.sessionStorage.setItem("authId", authId);
      window.sessionStorage.setItem("userId", personData.person._id);
      clearSigningSession();

      personData.person.sessionToken = authId;
      personData.person.identityNoLong = formattedPin;

      this.safeSetState({
        loginProcessActive: false,
        isLoading: false,
        loginSuccess: true,
        loginReturnPath: consumeLoginReturnPath() || "/projects",
      });
      this.props.setUser(personData.person);
    } catch (error) {
      this.safeSetState({
        errorMsg: "Något gick fel, försök igen.",
        isLoading: false,
      });
    }
  }

  cancelLoginProcess() {
    this.safeSetState({
      loginProcessActive: false,
      loginProcessFailed: false,
      bankIdMessage: "Legitimera dig i din BankID-app.",
    });
  }

  cancelBankID = () => {
    this.startAbortController?.abort();
    this.clearIntervals();
    this.clearStoredOrderRef();
    this.safeSetState({
      qrData: undefined,
      loginProcessActive: false,
      loginProcessFailed: false,
      isLoading: false,
      orderRef: "",
      errorMsg: "",
    });
  };

  failAuth = (message: string, reason: string, diagnostics: any = {}) => {
    const failedOrderRef = this.state.orderRef;

    this.clearIntervals();
    this.clearStoredOrderRef();
    this.safeSetState({
      errorMsg: message,
      loginProcessFailed: true,
      loginProcessActive: false,
      isLoading: false,
      qrData: undefined,
      orderRef: "",
    });

    logEvent("login_auth_failed", {
      reason,
      same_device: this.state.sameDevice ? "true" : "false",
      platform: this.lastPlatform || "",
    }).catch(() => false);

    this.reportClientError(reason, message, failedOrderRef, diagnostics);
  };

  reportClientError = (
    reason: string,
    message: string,
    orderRef: string,
    diagnostics: any = {},
  ) => {
    const controller = new AbortController();

    LimeAPI.post(
      "authenticateUser/logClientError",
      {
        reason,
        message,
        endpoint: "scrive/authenticateUser",
        sameDevice: this.state.sameDevice ? "true" : "false",
        platform: this.lastPlatform || "",
        orderRef: orderRef || "",
        httpStatus:
          diagnostics.status != null ? String(diagnostics.status) : "",
        responseSnippet: diagnostics.bodySnippet || "",
        errorName: diagnostics.errorName || "",
      },
      controller.signal,
    ).catch(() => false);
  };

  retryAuth = () => {
    this.clearIntervals();
    this.clearStoredOrderRef();
    this.safeSetState({
      errorMsg: "",
      loginProcessFailed: false,
      loginProcessActive: false,
      isLoading: false,
      qrData: undefined,
      orderRef: "",
      bankIdMessage: "",
    });
  };

  handleInputCallback(obj: { pin: string; inputValid: boolean }) {
    this.safeSetState({ pin: obj.pin, inputValid: obj.inputValid });

    if (this.state.rememberMe) {
      Preferences.set({ key: "userPin", value: obj.pin }).catch();
    } else {
      Preferences.remove({ key: "userPin" }).catch();
    }
  }

  handleToggleCallback(checked: boolean) {
    const newState: any = {
      rememberMe: checked,
    };

    if (!checked) {
      newState.pin = "";
    }

    this.safeSetState(newState);
  }

  startIntervals = (orderRef?: string) => {
    this.clearIntervals();

    const pollOrderRef = orderRef || this.state.orderRef;

    const poll = () => {
      this.checkLoginStatus(pollOrderRef, true);
    };

    poll();
    this.intervalId = setInterval(poll, 1000);

    this.timeoutId = setTimeout(() => {
      this.clearStoredOrderRef();
      this.safeSetState({
        qrData: undefined,
        loginProcessActive: false,
        isLoading: false,
        orderRef: "",
      });
      this.clearIntervals();
    }, this.ORDER_REF_MAX_AGE_MS);
  };

  clearIntervals = () => {
    clearInterval(this.intervalId);
    clearTimeout(this.timeoutId);
    clearInterval(this.checkOrderRefStatus);
    clearTimeout(this.redirectTimeoutId);
    clearTimeout(this.authTimeoutId);
    this.intervalId = null;
    this.timeoutId = null;
    this.checkOrderRefStatus = null;
    this.redirectTimeoutId = null;
    this.authTimeoutId = null;
  };

  renderWaitingMarkup() {
    return (
      <>
        <IonRow class="ion-justify-content-center ion-align-items-center ion-padding-top">
          <IonSpinner key="spinner" name="crescent" />
        </IonRow>
        <IonRow class="ion-justify-content-center ion-align-items-center ion-padding-top">
          <IonText className="ion-text-center">
            {this.state.bankIdMessage}
          </IonText>
        </IonRow>
        <IonRow class="ion-justify-content-center ion-align-items-center ion-padding-top">
          <IonButton
            onClick={this.cancelBankID}
            key="cancelSameDevice"
            color="dark"
            expand="block"
          >
            Avbryt
          </IonButton>
        </IonRow>
      </>
    );
  }

  renderFailedMarkup() {
    return (
      <>
        <IonRow class="ion-justify-content-center ion-align-items-center ion-padding-top">
          <IonNote className="login-failed-text ion-text-center" color="danger">
            {this.state.errorMsg || "Inloggningen misslyckades. Försök igen."}
          </IonNote>
        </IonRow>
        <IonRow class="ion-justify-content-center ion-align-items-center ion-padding-top">
          <IonButton
            onClick={this.retryAuth}
            key="retryBankID"
            color="primary"
            expand="block"
          >
            Försök igen
          </IonButton>
        </IonRow>
      </>
    );
  }

  render() {
    let markup;

    if (this.state.loginProcessFailed || this.state.errorMsg) {
      markup = this.renderFailedMarkup();
    } else if (this.state.qrData) {
      markup = (
        <IonGrid>
          <IonRow class="ion-justify-content-center ion-align-items-center">
            <IonText className="login-message ion-text-center">
              {this.state.bankIdMessage}
            </IonText>
          </IonRow>
          <IonRow class="ion-justify-content-center ion-align-items-center ion-padding-top">
            <QRCodeSVG value={this.state.qrData} size={200} level="M" />
          </IonRow>
          <IonRow class="ion-justify-content-center ion-align-items-center ion-padding-top">
            <IonButton
              onClick={this.cancelBankID}
              key="cancelQrCode"
              color="dark"
              expand="block"
            >
              Avbryt
            </IonButton>
          </IonRow>
        </IonGrid>
      );
    } else if (this.state.isLoading || this.state.loginProcessActive) {
      markup = this.renderWaitingMarkup();
    } else {
      markup = (
        <div key="bankIDbuttons">
          <IonButton
            onClick={() => this.bankID(true)}
            color="primary"
            expand="block"
            key="sameDeviceBankID"
          >
            Logga in med BankID
          </IonButton>
          <IonButton
            onClick={() => this.bankID(false)}
            color="dark"
            expand="block"
            class="ion-text-wrap"
            key="otherDeviceBankID"
          >
            Logga in med BankID på annan enhet
          </IonButton>
        </div>
      );
    }

    if (this.state.loginSuccess) {
      return (
        <>
          {markup}
          <Redirect to={this.state.loginReturnPath || "/projects"} />
        </>
      );
    }

    return markup;
  }
}

function mapStateToProps(state: RootState): StateProps {
  return {
    user: state.user,
  };
}

const mapDispatchToProps = {
  setUser: (user: APIUserInterface) => actions.user.setUser(user),
};

const LoginFormWithLifeCycle = withIonLifeCycle(LoginForm);
const ConnectedLoginForm = connect<
  StateProps,
  DispatchProps,
  OwnProps,
  RootState
>(mapStateToProps, mapDispatchToProps)(LoginFormWithLifeCycle);
const LoginFormWithRouter = withRouter(ConnectedLoginForm);

export default LoginFormWithRouter;
